import type {
  ContentBlock,
  ContentBlockId,
  ContentBlockKind,
  ContentTag,
} from "./types";
import type { ContentRelationship, RelationshipType } from "./relationships";
import { withInteractionPresets } from "./interactionPresets";

// ─── Storage ─────────────────────────────────────────────────────────

const blocks = new Map<ContentBlockId, ContentBlock>();
const relationships: ContentRelationship[] = [];
const tagIndex = new Map<ContentTag, Set<ContentBlockId>>();
const kindIndex = new Map<ContentBlockKind, Set<ContentBlockId>>();

// ─── Locale variants ─────────────────────────────────────────────────
// Per-locale field overrides keyed by block id. At lookup time the active
// locale's variant (if any) is shallow-merged onto the base block.

// Loose by design: variants are per-block-kind and type-safety lives in the
// per-kind registration helpers (frMoonArticle, frCondition, etc.). The
// registry only knows how to merge fields back onto the base block.
type LocaleVariant = {
  title?: string;
  subtitle?: string;
  body?: unknown;
};
const localeVariants = new Map<ContentBlockId, Map<string, LocaleVariant>>();
let activeLocale = "en";

export function registerLocaleVariant(
  id: ContentBlockId,
  locale: string,
  variant: LocaleVariant,
): void {
  let perLocale = localeVariants.get(id);
  if (!perLocale) {
    perLocale = new Map();
    localeVariants.set(id, perLocale);
  }
  perLocale.set(locale, variant);
}

export function setActiveContentLocale(locale: string): void {
  activeLocale = locale;
}

function applyLocale<T extends ContentBlock>(block: T): T {
  const variant =
    activeLocale === "en"
      ? undefined
      : localeVariants.get(block.id)?.get(activeLocale);
  let merged = block;
  if (variant) {
    // Shallow merge top-level fields, but if both block and variant supply a
    // `body` object, merge those one level deep so callers can override (e.g.)
    // `title` and `sections` without dropping `references` or `relatedArticles`.
    merged = { ...block, ...variant } as T;
    if (
      variant.body &&
      typeof variant.body === "object" &&
      block.body &&
      typeof block.body === "object"
    ) {
      (merged as ContentBlock).body = {
        ...(block.body as object),
        ...(variant.body as object),
      } as ContentBlock["body"];
    }
  }
  return withInteractionPresets(merged, activeLocale) as T;
}

// ─── Registration ────────────────────────────────────────────────────

export function registerBlock(block: ContentBlock): void {
  const normalizedBlock = block;
  blocks.set(normalizedBlock.id, normalizedBlock);

  for (const tag of normalizedBlock.tags) {
    let set = tagIndex.get(tag);
    if (!set) {
      set = new Set();
      tagIndex.set(tag, set);
    }
    set.add(normalizedBlock.id);
  }

  let kindSet = kindIndex.get(normalizedBlock.kind);
  if (!kindSet) {
    kindSet = new Set();
    kindIndex.set(normalizedBlock.kind, kindSet);
  }
  kindSet.add(normalizedBlock.id);
}

export function registerRelationship(rel: ContentRelationship): void {
  relationships.push(rel);
}

// ─── Query API ───────────────────────────────────────────────────────

export function getBlock(id: ContentBlockId): ContentBlock | undefined {
  const block = blocks.get(id);
  return block ? applyLocale(block) : undefined;
}

export function getBlockOrThrow(id: ContentBlockId): ContentBlock {
  const block = blocks.get(id);
  if (!block) throw new Error(`Content block not found: ${id}`);
  return applyLocale(block);
}

export function getBlocksByTag(tag: ContentTag): ContentBlock[] {
  const ids = tagIndex.get(tag);
  if (!ids) return [];
  return Array.from(ids)
    .map((id) => blocks.get(id))
    .filter((b): b is ContentBlock => b !== undefined)
    .map(applyLocale);
}

export function getBlocksByKind<K extends ContentBlockKind>(
  kind: K,
): ContentBlock<K>[] {
  const ids = kindIndex.get(kind);
  if (!ids) return [];
  return Array.from(ids)
    .map((id) => blocks.get(id))
    .filter((b): b is ContentBlock<K> => b !== undefined)
    .map(applyLocale);
}

export function getRelatedBlocks(
  id: ContentBlockId,
  type?: RelationshipType,
): { block: ContentBlock; relationship: ContentRelationship }[] {
  return relationships
    .filter((r) => (r.from === id || r.to === id) && (!type || r.type === type))
    .map((r) => ({
      block: blocks.get(r.from === id ? r.to : r.from),
      relationship: r,
    }))
    .filter(
      (r): r is { block: ContentBlock; relationship: ContentRelationship } =>
        r.block !== undefined,
    )
    .map((r) => ({ ...r, block: applyLocale(r.block) }));
}

export function getChildBlocks(id: ContentBlockId): ContentBlock[] {
  return relationships
    .filter((r) => r.from === id && r.type === "contains")
    .map((r) => blocks.get(r.to))
    .filter((b): b is ContentBlock => b !== undefined)
    .map(applyLocale);
}

export function getAllBlocks(): ContentBlock[] {
  return Array.from(blocks.values()).map(applyLocale);
}

export function getAllRelationships(): ContentRelationship[] {
  return [...relationships];
}

export function getAllTags(): ContentTag[] {
  return Array.from(tagIndex.keys());
}

export function getAllKinds(): ContentBlockKind[] {
  return Array.from(kindIndex.keys());
}

export function getModulesForProgram(
  programId: ContentBlockId,
): ContentBlock<"module">[] {
  return relationships
    .filter((r) => r.from === programId && r.type === "contains")
    .map((r) => blocks.get(r.to))
    .filter(
      (b): b is ContentBlock<"module"> =>
        b !== undefined && b.kind === "module",
    )
    .map(applyLocale);
}

export function getProgramsContainingModule(
  moduleId: ContentBlockId,
): ContentBlock<"program">[] {
  return relationships
    .filter((r) => r.to === moduleId && r.type === "contains")
    .map((r) => blocks.get(r.from))
    .filter(
      (b): b is ContentBlock<"program"> =>
        b !== undefined && b.kind === "program",
    )
    .map(applyLocale);
}
